import { useState } from "react";
import { Sparkles, Loader2, Skull, ShieldCheck, Save } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CategoryBadge, SeverityBadge } from "@/components/Badges";
import { CodeBlock } from "@/components/CodeBlock";
import { LabMeta, ToolsNeeded, Walkthrough } from "@/components/LabBrief";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const EXAMPLES = [
  "DNS cache poisoning",
  "Server-Side Request Forgery (SSRF)",
  "JWT token forgery",
  "Kerberoasting",
  "Clickjacking",
];

const StepList = ({ steps, accent }) => (
  <div className="space-y-3">
    {steps?.map((s, i) => (
      <div key={i} className="pl-4 border-l-2" style={{ borderColor: accent }}>
        <h4 className="font-mono font-semibold text-sm text-slate-100">{s.title}</h4>
        <p className="text-sm text-slate-400 mt-1">{s.description}</p>
        {s.code ? <CodeBlock code={s.code} accent={accent} label="snippet" /> : null}
      </div>
    ))}
  </div>
);

export default function AIGenerator() {
  const { user } = useAuth();
  const [topic, setTopic] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  const generate = async () => {
    if (!topic.trim()) return;
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/ai/generate", { topic });
      setResult(data);
      toast.success("Tutorial generated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Generation failed");
    } finally {
      setLoading(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/tutorials", result);
      toast.success("Saved to database");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
      <div className="flex items-center gap-2 mb-2">
        <Sparkles className="text-[#a855f7]" size={24} />
        <h1 className="font-mono text-3xl font-extrabold">AI Tutorial Generator</h1>
      </div>
      <p className="font-mono text-sm text-slate-500 mb-8">
        // enter an attack or vulnerability — get a full breakdown, a safe lab walkthrough you can run, and the defenses
      </p>

      <div data-testid="ai-generator-form" className="glass rounded-lg border border-[#a855f7]/30 p-5 mb-8">
        <div className="flex flex-col sm:flex-row gap-3">
          <Input
            data-testid="ai-topic-input"
            placeholder="e.g. Server-Side Request Forgery"
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && generate()}
            className="font-mono text-sm bg-[#0b111d] border-slate-800 text-slate-100"
          />
          <Button
            data-testid="ai-generate-button"
            onClick={generate}
            disabled={loading || !topic.trim()}
            className="bg-[#a855f7] hover:bg-[#9333ea] text-white font-mono font-bold px-6 whitespace-nowrap"
          >
            {loading ? <><Loader2 size={16} className="animate-spin mr-2" /> generating</> : <><Sparkles size={16} className="mr-2" /> Generate</>}
          </Button>
        </div>
        <div className="flex flex-wrap gap-2 mt-4">
          <span className="font-mono text-[10px] text-slate-600 uppercase tracking-widest">try:</span>
          {EXAMPLES.map((ex) => (
            <button
              key={ex}
              onClick={() => setTopic(ex)}
              className="font-mono text-[11px] text-slate-400 border border-slate-800 rounded px-2 py-0.5 hover:border-[#a855f7]/50 hover:text-[#a855f7] transition-colors"
            >
              {ex}
            </button>
          ))}
        </div>
      </div>

      {loading && (
        <div className="text-center py-16 font-mono text-[#a855f7] cursor-blink">analyzing attack surface</div>
      )}

      {result && (
        <div data-testid="ai-result" className="fade-up">
          <div className="glass neon-border rounded-lg p-6 mb-6">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex flex-wrap items-center gap-2">
                <CategoryBadge category={result.category} />
                <SeverityBadge severity={result.severity} />
                {result.cve ? <span className="font-mono text-[10px] px-2 py-1 rounded border border-slate-700 text-slate-400">{result.cve}</span> : null}
              </div>
              {user && (
                <Button
                  data-testid="ai-save-button"
                  onClick={save}
                  disabled={saving}
                  className="bg-[#00ff66] hover:bg-[#00e65c] text-[#04120a] font-mono font-bold"
                >
                  {saving ? <Loader2 size={15} className="animate-spin mr-2" /> : <Save size={15} className="mr-2" />}
                  Save to DB
                </Button>
              )}
            </div>
            <h2 className="font-mono text-2xl font-extrabold mt-4">{result.title}</h2>
            <p className="text-slate-300 mt-3 leading-relaxed">{result.summary}</p>
          </div>

          <LabMeta difficulty={result.difficulty} time={result.time_estimate} prerequisites={result.prerequisites} />

          <section className="mb-10">
            <div className="flex items-center gap-2 mb-4">
              <Skull className="text-[#ef4444]" size={18} />
              <h3 className="font-mono text-xl font-bold text-[#ef4444]">How it works</h3>
            </div>
            <StepList steps={result.how_it_works} accent="#ef4444" />
            {result.attack_code ? <div className="mt-4"><CodeBlock code={result.attack_code} accent="#ff5a5a" label="exploit" /></div> : null}
          </section>

          <ToolsNeeded tools={result.tools} />

          <Walkthrough steps={result.walkthrough} />

          <section className="rounded-lg border border-[#00ff66]/30 bg-[#00ff66]/[0.03] p-6">
            <div className="flex items-center gap-2 mb-4">
              <ShieldCheck className="text-[#00ff66]" size={18} />
              <h3 className="font-mono text-xl font-bold text-[#00ff66]">How to protect yourself</h3>
            </div>
            <StepList steps={result.defense_steps} accent="#00ff66" />
            {result.defense_code ? <div className="mt-4"><CodeBlock code={result.defense_code} accent="#00ff66" label="defense" /></div> : null}
          </section>

          {!user && (
            <p className="font-mono text-xs text-slate-600 mt-4">// log in as admin to save this tutorial to the database</p>
          )}
        </div>
      )}
    </div>
  );
}
